// API layer for the stops bulk importer. Uses the admin's shared axios instance
// (auth header flows from localStorage.authToken like every other screen).
//
// Hard rules (from the verified import plan — see TOUR_UPLOAD_AUTOMATION_PLAN.md):
//  - Upload success is HTTP 200 AND a non-empty data.url[0].path. The upload
//    endpoints return `success: true` even on their failure branch, so body
//    flags are never trusted.
//  - 401 -> AuthExpiredError (pause + resume). 403 -> PermissionDeniedError.
//    429 -> exponential backoff retry (safe: the limiter rejects before handlers).
//  - tourPoints/create is NEVER blindly retried on a network error — the server
//    may have processed it; a resend would duplicate the stop (duplicates share
//    Spaces audio files and cannot be safely deleted). Callers get
//    NetworkUncertainError and must reconcile before resending.
//  - /tours/generateAudioTranscribe is deliberately not exposed here (it
//    re-submits every file to AssemblyAI inline with no skip-existing check).

import instance from "../services/InstanceAxios";

export class AuthExpiredError extends Error {}
export class PermissionDeniedError extends Error {}
export class UploadRejectedError extends Error {}
export class NetworkUncertainError extends Error {}

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

function classifyStatus(status: number | undefined, path: string): Error | null {
  if (status === 401) return new AuthExpiredError(`Session expired (401 on ${path}). Log in again, then Resume.`);
  if (status === 403) return new PermissionDeniedError(`This account lacks admin permission for ${path} (403).`);
  return null;
}

async function requestJson(
  method: "get" | "post" | "put",
  path: string,
  body?: any,
  opts: { tries?: number; retryNetwork?: boolean } = {}
): Promise<any> {
  const { tries = 5, retryNetwork = true } = opts;
  let attempt = 0;
  for (;;) {
    attempt++;
    try {
      const res = method === "get" ? await instance.get(path) : await (instance as any)[method](path, body);
      return res.data;
    } catch (e: any) {
      const status = e?.response?.status;
      const authErr = classifyStatus(status, path);
      if (authErr) throw authErr;
      if (status === 429) {
        if (attempt >= tries) throw new Error(`Rate-limited on ${path} after ${tries} tries`);
        await sleep(2000 * 2 ** (attempt - 1));
        continue;
      }
      if (status) {
        // server responded with a non-retryable error
        throw new Error(`${path} -> HTTP ${status}: ${JSON.stringify(e?.response?.data)?.slice(0, 200)}`);
      }
      // no response at all = network error
      if (!retryNetwork) throw new NetworkUncertainError(`${method.toUpperCase()} ${path}: ${e?.message || "network error"}`);
      if (attempt >= tries) throw e;
      await sleep(2000 * attempt);
    }
  }
}

async function uploadFile(
  endpoint: string,
  fieldName: "image" | "audio",
  filename: string,
  data: Uint8Array,
  onProgress?: (pct: number) => void
): Promise<string> {
  const tries = 4;
  let attempt = 0;
  for (;;) {
    attempt++;
    try {
      const form = new FormData();
      form.append(fieldName, new Blob([data as any]), filename);
      const res = await instance.post(endpoint, form, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (ev: any) => {
          if (onProgress && ev.total) onProgress(Math.round((ev.loaded * 100) / ev.total));
        },
      });
      const path = res?.data?.data?.url?.[0]?.path;
      if (res.status === 200 && typeof path === "string" && path.length > 0) return path;
      if (attempt >= tries) throw new UploadRejectedError(`Upload rejected for ${filename}: HTTP ${res.status}`);
    } catch (e: any) {
      if (e instanceof UploadRejectedError) throw e;
      const status = e?.response?.status;
      const authErr = classifyStatus(status, endpoint);
      if (authErr) throw authErr;
      if (attempt >= tries) {
        throw new UploadRejectedError(
          `Upload failed for ${filename}: ${status ? `HTTP ${status}` : e?.message} ${JSON.stringify(e?.response?.data)?.slice(0, 150) || ""}`
        );
      }
      await sleep(status === 429 ? 2000 * 2 ** (attempt - 1) : 1500 * attempt);
    }
  }
}

export const importApi = {
  uploadImage: (filename: string, data: Uint8Array, onProgress?: (pct: number) => void) =>
    uploadFile("api/imagesUpload", "image", filename, data, onProgress),
  uploadAudio: (filename: string, data: Uint8Array, onProgress?: (pct: number) => void) =>
    uploadFile("api/audiosUpload", "audio", filename, data, onProgress),

  getAllTours: async (): Promise<any[]> => {
    const d = await requestJson("get", "api/tours/list");
    return d?.data ?? d ?? [];
  },

  // NOTE: refreshes the tour's detail file in Spaces as a designed side effect
  // (same as the app opening the tour). Only call with confirmed-real tour ids.
  getTour: async (tourId: number): Promise<any> => {
    const d = await requestJson("post", "api/tours/list", { tourId });
    const data = d?.data ?? d;
    return Array.isArray(data) ? data[0] : data;
  },

  createStop: async (payload: any): Promise<any> => {
    const d = await requestJson("post", "api/tourPoints/create", payload, { retryNetwork: false });
    return d?.data ?? d;
  },

  generateRoute: (tourId: number) => requestJson("post", "api/singleTourDirection", { tourId }),
  bumpOffline: (tourId: number) => requestJson("get", `api/tours/updateOffline/${tourId}`),
  downloadManifest: async (tourId: number, langs: string[]): Promise<any> =>
    requestJson("get", `api/tours/downloadManifest?tourId=${tourId}&langs=${langs.join(",")}`),

  // Direct CDN reachability check — the Spaces bucket serves
  // access-control-allow-origin: * with HEAD allowed (verified), so this works
  // from the browser exactly like the CLI sweep.
  headOk: async (url: string): Promise<boolean> => {
    try {
      const res = await fetch(url, { method: "HEAD" });
      return res.status === 200;
    } catch {
      return false;
    }
  },
};
